/**
 * TwilioVoiceContext
 *
 * Wires the Twilio Voice JS SDK (@twilio/voice-sdk) into the React tree so the
 * browser can place and receive PSTN calls through the user's NC/state Twilio
 * number. This is what was missing — without it, /twilio/make-call had no
 * audio leg, the external phone rang into a silent <Dial> loop, and there was
 * no way to end the call from the browser.
 *
 * Lifecycle:
 *   - On login → fetch /twilio/client-token → new Device(token).
 *   - Device emits 'incoming' for inbound PSTN-via-<Client> calls (the backend
 *     /voice/incoming TwiML uses <Dial><Client>user_id</Client></Dial>).
 *   - placeCall(toNumber, state) → device.connect({ params: { To, state, user_id } }).
 *     Twilio fetches /api/twilio/voice/outgoing for the TwiML which dials the
 *     external number with the state's Twilio number as caller_id.
 *   - On logout → device.unregister() + device.destroy().
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Device } from '@twilio/voice-sdk';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { useTwilioNotifications } from './TwilioNotificationContext';

const TwilioVoiceContext = createContext(null);
export const useTwilioVoice = () => useContext(TwilioVoiceContext);

export const TwilioVoiceProvider = ({ children }) => {
  const { api, user, isAuthenticated } = useAuth();
  const notif = useTwilioNotifications();
  const deviceRef = useRef(null);
  const activeCallRef = useRef(null);
  // What the rest of the app subscribes to
  const [deviceState, setDeviceState] = useState({ ready: false, error: null });
  const [activeCall, setActiveCall] = useState(null); // { call, direction, to, from, contactName }
  const [incoming, setIncoming] = useState(null);     // { call, from }

  // Custom incoming-call ringtone.
  // Web Audio oscillator approach didn't reliably play across all
  // browsers — and the SDK's native ringer is sporadic too. Best
  // pattern: an HTML5 <audio loop> element loading a real generated
  // ringtone WAV (NA cadence: 440Hz+480Hz, 2s on, 4s off, 6s loop).
  // We pre-warm it on every user gesture so play() is allowed by
  // autoplay policy when the call comes in.
  const ringtoneRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    // Public/unauthenticated routes (e.g. /card/:userId) must NEVER load
    // a ringtone or attach window-wide click listeners that briefly play
    // audio — visitors of the public business card were hearing the
    // outbound dial tone on first tap. Bail entirely until login.
    if (!isAuthenticated || !user) return undefined;
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    const audio = new Audio(`${apiBase}/api/media/calling/ringtone_incoming.wav`);
    audio.loop = true;
    audio.preload = 'auto';
    audio.volume = 0.6;
    ringtoneRef.current = audio;

    // ROOT-CAUSE FIX: the previous implementation .play()+.pause()'d
    // the actual ringtone/disclaimer file during the first user click
    // (with muted=true + volume=0 as a safety net). That was still
    // producing audible clicks on iOS/Safari where muted+volume-0 are
    // not fully respected before play() actually starts decoding.
    //
    // We now NEVER touch the real audio file here. We only:
    //   1. Unlock the browser's audio policy by playing a 1-sample
    //      silent AudioContext buffer.
    //   2. Resume any suspended AudioContext.
    //
    // Later, when there is a real need to play the ringtone (a true
    // incoming call), a freshly-constructed Audio() element .play()
    // is allowed because the AudioContext has been unlocked inside a
    // prior user gesture. If an individual browser still blocks the
    // play, we fall through gracefully — the call continues.
    let warmed = false;
    const warm = () => {
      if (warmed) return;
      warmed = true;
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        const ctx = new AC();
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        const buf = ctx.createBuffer(1, 1, 22050);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(ctx.destination);
        src.start(0);
      } catch (e) { /* ignore */ }
    };
    window.addEventListener('click', warm, { capture: true });
    window.addEventListener('keydown', warm, { capture: true });
    window.addEventListener('touchstart', warm, { capture: true });
    return () => {
      window.removeEventListener('click', warm, { capture: true });
      window.removeEventListener('keydown', warm, { capture: true });
      window.removeEventListener('touchstart', warm, { capture: true });
      try { audio.pause(); } catch (e) { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);

  const startRingtone = useCallback(() => {
    const a = ringtoneRef.current;
    if (!a) return;
    try {
      a.currentTime = 0;
      const p = a.play();
      if (p && typeof p.then === 'function') {
        p.catch((e) => console.warn('[TwilioVoice] ringtone.play() blocked:', e?.name, e?.message));
      }
    } catch (e) { console.warn('[TwilioVoice] startRingtone:', e); }
  }, []);

  const stopRingtone = useCallback(() => {
    const a = ringtoneRef.current;
    if (!a) return;
    try { a.pause(); a.currentTime = 0; } catch (e) { /* ignore */ }
  }, []);

  // ------- Initialise / tear down the Device with auth lifecycle -------
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      if (!isAuthenticated || !user) return;
      try {
        const tokenRes = await api.get('/twilio/client-token');
        if (cancelled) return;
        const token = tokenRes.data?.token;
        if (!token) {
          setDeviceState({ ready: false, error: 'No Twilio token returned' });
          return;
        }
        // Tear any prior device down before instantiating a new one (handles
        // hot reload / re-login).
        try { deviceRef.current?.destroy(); } catch (e) { /* ignore */ }

        const device = new Device(token, {
          // Lower log noise in production
          logLevel: 1,
          // Closer-to-zero ICE-restart-style behaviour for nicer reliability:
          allowIncomingWhileBusy: true,
          // Codec preference — opus first, fall back to PCMU
          codecPreferences: ['opus', 'pcmu'],
        });

        device.on('registered', () => {
          if (cancelled) return;
          console.log('[TwilioVoice] Device REGISTERED, identity:', device.identity);
          // We play our own custom HTML5 ringtone for incoming calls,
          // BUT we leave the SDK's native incoming sound enabled too —
          // disabling it via `device.audio.incoming(false)` had a side
          // effect of the SDK rejecting incoming calls with no-answer
          // in some builds. The user might hear two short overlapping
          // tones briefly, which is far better than calls instantly
          // going to voicemail.
          try { device.audio?.outgoing?.(true); } catch (e) { /* ignore */ }
          try { device.audio?.disconnect?.(true); } catch (e) { /* ignore */ }
          // Echo cancellation via setAudioConstraints (no breaking
          // side effects observed). Chrome's getUserMedia probe
          // also sets these explicitly inside acceptIncoming /
          // placeCall so the mic stream itself is EC-enabled.
          try {
            device.audio?.setAudioConstraints?.({
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            });
          } catch (e) { /* ignore */ }
          setDeviceState({ ready: true, error: null });
        });
        device.on('error', (e) => {
          console.error('[TwilioVoice] Device error:', e?.code, e?.message, e);
          // Non-fatal — most "31000" series errors are transient signalling.
          setDeviceState((s) => ({ ...s, error: e?.message || String(e) }));
        });
        device.on('incoming', (call) => {
          // PSTN call routed to this client identity via <Dial><Client>.
          console.log('[TwilioVoice] INCOMING call from:', call.parameters?.From, 'CallSid:', call.parameters?.CallSid, 'callerInfo:', call.parameters);
          // Defensive: clear any stale active-call data so a previous
          // call's twilio_call_sid / conference_sid can NEVER bleed into
          // this new call's UI. This is the "Simon clicked Add on test
          // #2 but sent test #1's call_sid" bug we saw in production
          // logs — even if hydrate fails for some reason, the UI must
          // never re-use stale data from a prior call.
          try { notif?.clearActiveCall?.(); } catch (e) { /* ignore */ }
          // Start the generated ringtone (Web Audio oscillator). Far
          // more reliable than playing an mp3 — no network fetch, no
          // autoplay policy block as long as the AudioContext was
          // unlocked on a prior user gesture.
          startRingtone();
          setIncoming({
            call,
            from: call.parameters?.From || call.parameters?.from || 'Unknown',
          });
          call.on('cancel', () => {
            console.warn('[TwilioVoice] incoming call CANCELLED by Twilio (caller hung up before accept). CallSid:', call.parameters?.CallSid);
            stopRingtone();
            setIncoming((cur) => (cur && cur.call === call ? null : cur));
          });
          call.on('disconnect', () => {
            console.warn('[TwilioVoice] incoming call DISCONNECTED. CallSid:', call.parameters?.CallSid);
            stopRingtone();
            setIncoming((cur) => (cur && cur.call === call ? null : cur));
            setActiveCall((cur) => (cur && cur.call === call ? null : cur));
            activeCallRef.current = null;
            try { notif?.clearActiveCall?.(); } catch (e) { /* ignore */ }
          });
          call.on('reject', () => {
            console.warn('[TwilioVoice] incoming call REJECT event. CallSid:', call.parameters?.CallSid);
            stopRingtone();
            setIncoming((cur) => (cur && cur.call === call ? null : cur));
          });
          call.on('error', (err) => {
            console.error('[TwilioVoice] incoming call ERROR:', err?.code, err?.message, err);
            stopRingtone();
          });
          call.on('accept', () => {
            console.log('[TwilioVoice] incoming call ACCEPT event fired (audio bridge live). CallSid:', call.parameters?.CallSid);
            stopRingtone();
          });
        });
        device.on('tokenWillExpire', async () => {
          try {
            const r = await api.get('/twilio/client-token');
            if (r.data?.token) device.updateToken(r.data.token);
          } catch (e) { console.warn('[TwilioVoice] token refresh failed:', e); }
        });

        await device.register();
        deviceRef.current = device;
      } catch (e) {
        console.error('[TwilioVoice] init failed:', e);
        if (!cancelled) setDeviceState({ ready: false, error: e?.message || String(e) });
      }
    };
    init();
    return () => {
      cancelled = true;
      try { activeCallRef.current?.disconnect(); } catch (e) { /* ignore */ }
      try { deviceRef.current?.destroy(); } catch (e) { /* ignore */ }
      deviceRef.current = null;
      activeCallRef.current = null;
      setDeviceState({ ready: false, error: null });
      setActiveCall(null);
      setIncoming(null);
    };
    // Re-init only when identity actually changes — not on every render.
    // Using `user` (object) here would tear down the device on every parent
    // re-render (e.g. notification poll), which on desktop manifests as
    // calls failing because the Device is being destroyed mid-connect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);

  // ------- Outbound ----------------------------------------------------
  const placeCall = useCallback(async (toNumber, opts = {}) => {
    const device = deviceRef.current;
    if (!device) {
      toast.error('Phone not ready yet — try again in a moment.');
      throw new Error('Twilio Device not initialised');
    }

    // Desktop Chrome/Edge/Firefox refuse to start a WebRTC peer connection
    // unless the page has been granted mic permission via a user gesture.
    // Acquiring (then immediately releasing) a mic stream here forces the
    // permission prompt into the click handler that called placeCall, which
    // is what the browser requires. If the user denies, surface a clear
    // toast instead of failing silently like we used to.
    try {
      const probe = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
      probe.getTracks().forEach((t) => t.stop());
    } catch (permErr) {
      const msg = permErr?.name === 'NotAllowedError'
        ? 'Microphone access was blocked. Allow it in your browser settings to make calls.'
        : 'Could not access your microphone. Check your audio device.';
      toast.error(msg);
      throw permErr;
    }

    const params = {
      To: toNumber,
      state: opts.state || user?.state || 'NC',
      user_id: user?.id || '',
      // When the caller toggled OFF the Call Recording & AI Summary
      // switches in CallsPage, `opts.record === false` reaches here.
      // Pass as a string so Twilio serialises it into the
      // /api/twilio/voice/outgoing form data — the backend uses this
      // to suppress recording, the disclaimer, and AI processing.
      record: opts.record === false ? 'false' : 'true',
    };
    const call = await device.connect({ params });
    activeCallRef.current = call;
    setActiveCall({
      call,
      direction: 'outbound',
      to: toNumber,
      contactName: opts.contactName || null,
      startedAt: Date.now(),
    });

    call.on('ringing', () => {
      setActiveCall((cur) => (cur ? { ...cur, status: 'ringing' } : cur));
    });
    call.on('accept', () => {
      setActiveCall((cur) => (cur ? { ...cur, status: 'in-progress', callSid: call.parameters?.CallSid } : cur));
      // NOTE: The call-start disclaimer is played server-side via the
      // Conference participant-join announceUrl (see voice/outgoing
      // Conference flow + /conference-event handler). Playing it
      // locally on 'accept' fired BEFORE the PSTN side answered and
      // the external party never heard it — that was the
      // user-reported bug.
    });
    call.on('disconnect', () => {
      setActiveCall((cur) => (cur && cur.call === call ? null : cur));
      activeCallRef.current = null;
    });
    call.on('cancel', () => {
      setActiveCall((cur) => (cur && cur.call === call ? null : cur));
      activeCallRef.current = null;
    });
    call.on('error', (e) => {
      console.error('[TwilioVoice] call error:', e);
      toast.error(e?.message || 'Call failed');
    });

    return call;
  }, [user?.id, user?.state, api]);

  const hangUp = useCallback(() => {
    try { activeCallRef.current?.disconnect(); } catch (e) { /* ignore */ }
    setActiveCall(null);
    activeCallRef.current = null;
  }, []);

  const acceptIncoming = useCallback(async () => {
    const inc = incoming;
    console.log('[TwilioVoice] acceptIncoming clicked. inc:', inc);
    if (!inc?.call) {
      console.warn('[TwilioVoice] No incoming call to accept');
      return;
    }
    // Ensure mic permission is unlocked under user-gesture (some Chromium
    // builds need this on incoming too, not just outgoing). Without an
    // active mic stream the SDK can't establish the WebRTC audio leg
    // and the call drops within ~3s of accept().
    try {
      const probe = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
      probe.getTracks().forEach((t) => t.stop());
      console.log('[TwilioVoice] mic probe ok');
    } catch (permErr) {
      console.error('[TwilioVoice] mic probe failed:', permErr?.name, permErr?.message);
      toast.error('Microphone access blocked — allow it and try again.');
      return;
    }
    // Disconnect any prior call so we don't have two concurrent SDK calls.
    if (activeCallRef.current && activeCallRef.current !== inc.call) {
      try { activeCallRef.current.disconnect(); } catch (e) { /* ignore */ }
      activeCallRef.current = null;
    }
    try {
      console.log('[TwilioVoice] Calling inc.call.accept()...');
      // Stop ringtone immediately on user accept (don't wait for SDK
      // 'accept' event which fires later after WebRTC negotiation).
      stopRingtone();
      inc.call.accept();
      console.log('[TwilioVoice] accept() returned (no exception)');
      const sdkLegSid = inc.call.parameters?.CallSid;
      setActiveCall({
        call: inc.call,
        direction: 'inbound',
        from: inc.from,
        contactName: null,
        startedAt: Date.now(),
        status: 'in-progress',
        callSid: sdkLegSid,
      });
      activeCallRef.current = inc.call;
      setIncoming(null);
      // Hydrate `activeExternalCall` with the EXACT external_calls row
      // for THIS SDK leg. The SDK leg's CallSid (inc.call.parameters.
      // CallSid) is what we stored as `pstn_conferences.user_call_sid`
      // when we dialed Simon into the conference, so the lookup is a
      // direct primary-key match — no time heuristics, no /active-calls
      // racing with concurrent inbound calls. Falls back to a /active-
      // calls scan only if the targeted lookup fails (legacy rows).
      const tryHydrate = async (attemptsLeft) => {
        if (attemptsLeft <= 0) return;
        if (sdkLegSid) {
          try {
            const r = await api.get(`/twilio/calls/by-sdk-leg/${encodeURIComponent(sdkLegSid)}`);
            const match = r.data;
            if (match && match.twilio_call_sid) {
              console.log('[TwilioVoice] adopted active-call by SDK leg: sid=', match.twilio_call_sid, 'conf=', match.conference_sid);
              notif?.setOutboundCall?.({ ...match, _sdkCall: inc.call });
              return;
            }
          } catch (e) {
            // 404 here just means the backend hasn't written
            // pstn_conferences.user_call_sid yet — retry shortly.
            if (e?.response?.status !== 404) {
              console.warn('[TwilioVoice] by-sdk-leg lookup failed:', e?.message);
            }
          }
        }
        setTimeout(() => tryHydrate(attemptsLeft - 1), 1000);
      };
      tryHydrate(6);
    } catch (e) {
      console.error('[TwilioVoice] Failed to accept incoming call:', e);
      toast.error(e?.message || 'Could not accept call');
    }
  }, [incoming, stopRingtone]);

  const rejectIncoming = useCallback(() => {
    try { incoming?.call?.reject(); } catch (e) { /* ignore */ }
    stopRingtone();
    setIncoming(null);
  }, [incoming, stopRingtone]);

  const muteToggle = useCallback(() => {
    const c = activeCallRef.current;
    if (!c) return;
    const next = !c.isMuted();
    c.mute(next);
    setActiveCall((cur) => (cur ? { ...cur, isMuted: next } : cur));
  }, []);

  const sendDigits = useCallback((digits) => {
    try { activeCallRef.current?.sendDigits(String(digits)); } catch (e) { /* ignore */ }
  }, []);

  const value = {
    deviceState,
    activeCall,
    incoming,
    placeCall,
    hangUp,
    acceptIncoming,
    rejectIncoming,
    muteToggle,
    sendDigits,
  };

  return (
    <TwilioVoiceContext.Provider value={value}>
      {children}
    </TwilioVoiceContext.Provider>
  );
};

export default TwilioVoiceProvider;
